import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import UserService from "../../../services/UserService";
import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";

const EMPTY_FORM = {
  direction_id: null,
  referentiel_id: null,
  pilote_id: null,
  leader_smq_id: null,
  mission: "",
  entrant: "",
  sortant: "",
  activite: "",
  ressource: "",
  indicateur: "",
  valeur: "",
};

const TEXT_FIELDS = [
  { key: "mission", label: "MISSION", fullWidth: true, rows: 3 },
  { key: "entrant", label: "ÉLÉMENTS ENTRANTS", rows: 3 },
  { key: "activite", label: "ACTIVITÉS PRINCIPALES", rows: 3 },
  { key: "sortant", label: "ÉLÉMENTS SORTANTS", rows: 3 },
  { key: "ressource", label: "RESSOURCES", rows: 3 },
  { key: "indicateur", label: "INDICATEURS DE PERFORMANCE", rows: 3 },
  {
    key: "valeur",
    label: "VALEUR ACTUELLE DES INDICATEURS",
    placeholder: "Ex. : 87 %, 42 dossiers…",
    rows: 2,
  },
];

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const getArray = (payload, label, allowEmpty = false) => {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (allowEmpty && payload && (payload.status === false || payload.status === 0)) {
    return [];
  }

  throw new Error(payload?.message || `Impossible de charger ${label}`);
};

const normalizeDirection = (direction) => ({
  code: String(direction?.direction_code || "").trim(),
  id: direction?.direction_id,
  label:
    [direction?.direction_code, direction?.direction_name]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" — ") || "Direction sans nom",
  name: String(direction?.direction_name || "").trim(),
});

const normalizeReferentiel = (referentiel) => ({
  categoryId: referentiel?.categorie_id,
  categoryName: String(referentiel?.categorie_name || "").trim(),
  code: String(referentiel?.referentiel_code || "").trim(),
  id: referentiel?.referentiel_id,
  label:
    [referentiel?.referentiel_code, referentiel?.referentiel_nom]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" — ") +
      (referentiel?.categorie_name ? ` [${referentiel.categorie_name}]` : "") ||
    "Référentiel sans nom",
  name: String(referentiel?.referentiel_nom || "").trim(),
});

const normalizeUser = (user) => {
  const composedName = [user?.first_name, user?.last_name]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

  return {
    id: user?.user_id,
    label:
      String(user?.name || user?.identity || "").trim() ||
      composedName ||
      "Utilisateur sans nom",
  };
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{ color: "#315272", fontSize: 10, fontWeight: 700 }}
  >
    {children}
  </Typography>
);

const ProcessusAdd = ({
  initialValues = null,
  mode = "create",
  onClose,
  onSaved,
  open,
  referentielCategory = null,
}) => {
  const isEditMode = mode === "edit";
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [directions, setDirections] = useState([]);
  const [referentiels, setReferentiels] = useState([]);
  const [pilotes, setPilotes] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [usedReferentielIds, setUsedReferentielIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [directionsError, setDirectionsError] = useState(null);
  const [referentielsError, setReferentielsError] = useState(null);
  const [pilotesError, setPilotesError] = useState(null);
  const [leadersError, setLeadersError] = useState(null);
  const [associationsError, setAssociationsError] = useState(null);
  const [directionsLoading, setDirectionsLoading] = useState(false);
  const [referentielsLoading, setReferentielsLoading] = useState(false);
  const [pilotesLoading, setPilotesLoading] = useState(false);
  const [leadersLoading, setLeadersLoading] = useState(false);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [baseReloadKey, setBaseReloadKey] = useState(0);
  const [directionReloadKey, setDirectionReloadKey] = useState(0);
  const baseRequestRef = useRef(0);
  const directionRequestRef = useRef(0);
  const directionInputRef = useRef(null);
  const referentielInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const requestId = baseRequestRef.current + 1;
    baseRequestRef.current = requestId;
    setDirectionsLoading(true);
    setReferentielsLoading(true);
    setLeadersLoading(true);
    setDirectionsError(null);
    setReferentielsError(null);
    setLeadersError(null);

    Promise.allSettled([
      ProcessusService.getDirections(),
      ProcessusService.getReferentiels(),
      UserService.getAllUserActive(),
    ]).then((results) => {
      if (requestId !== baseRequestRef.current) {
        return;
      }

      const [directionsResult, referentielsResult, leadersResult] = results;

      try {
        if (directionsResult.status === "rejected") {
          throw directionsResult.reason;
        }
        setDirections(
          getArray(directionsResult.value, "les directions")
            .map(normalizeDirection)
            .filter((option) => option.id !== null && option.id !== undefined)
        );
      } catch (error) {
        setDirections([]);
        setDirectionsError(error?.message || "Impossible de charger les directions");
      } finally {
        setDirectionsLoading(false);
      }

      try {
        if (referentielsResult.status === "rejected") {
          throw referentielsResult.reason;
        }
        setReferentiels(
          getArray(referentielsResult.value, "les référentiels")
            .map(normalizeReferentiel)
            .filter((option) => option.id !== null && option.id !== undefined)
        );
      } catch (error) {
        setReferentiels([]);
        setReferentielsError(
          error?.message || "Impossible de charger les référentiels"
        );
      } finally {
        setReferentielsLoading(false);
      }

      try {
        if (leadersResult.status === "rejected") {
          throw leadersResult.reason;
        }
        setLeaders(
          getArray(leadersResult.value, "les leaders SMQ", true)
            .map(normalizeUser)
            .filter((option) => option.id !== null && option.id !== undefined)
        );
      } catch (error) {
        setLeaders([]);
        setLeadersError(error?.message || "Impossible de charger les leaders SMQ");
      } finally {
        setLeadersLoading(false);
      }
    });

    return () => {
      baseRequestRef.current += 1;
    };
  }, [baseReloadKey, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormData({
      ...EMPTY_FORM,
      processus_id: initialValues?.processus_id ?? null,
      direction_id: initialValues?.direction_id ?? null,
      referentiel_id: initialValues?.referentiel_id ?? null,
      pilote_id: initialValues?.pilote_id ?? null,
      leader_smq_id: initialValues?.leader_smq_id ?? null,
      mission: initialValues?.mission || "",
      entrant: initialValues?.entrant || "",
      sortant: initialValues?.sortant || "",
      activite: initialValues?.activite || "",
      ressource: initialValues?.ressource || "",
      indicateur: initialValues?.indicateur || "",
      valeur: initialValues?.valeur || "",
    });
    setUsedReferentielIds([]);
    setPilotes([]);
    setErrors({});
    setSaveError(null);
    setAssociationsError(null);
    setPilotesError(null);
    setSaving(false);
  }, [initialValues, open]);

  useEffect(() => {
    const directionId = formData.direction_id;

    if (!open || !directionId) {
      directionRequestRef.current += 1;
      setUsedReferentielIds([]);
      setPilotes([]);
      setAssociationsLoading(false);
      setPilotesLoading(false);
      return undefined;
    }

    const requestId = directionRequestRef.current + 1;
    directionRequestRef.current = requestId;
    setAssociationsLoading(true);
    setPilotesLoading(true);
    setAssociationsError(null);
    setPilotesError(null);

    Promise.allSettled([
      ProcessusService.getProcessusByDirection(directionId),
      UserService.getAllUserByDirection(directionId),
    ]).then((results) => {
      if (requestId !== directionRequestRef.current) {
        return;
      }

      const [processusResult, pilotesResult] = results;

      try {
        if (processusResult.status === "rejected") {
          throw processusResult.reason;
        }
        const existingProcessus = getArray(
          processusResult.value,
          "les processus existants"
        );
        const usedIds = existingProcessus
          .filter(
            (processus) =>
              !isEditMode ||
              !sameId(processus.processus_id, formData.processus_id)
          )
          .map((processus) => processus.referentiel_id);
        setUsedReferentielIds(usedIds);
        setFormData((current) =>
          !isEditMode &&
          usedIds.some((id) => sameId(id, current.referentiel_id))
            ? { ...current, referentiel_id: null }
            : current
        );
      } catch (error) {
        setUsedReferentielIds([]);
        setAssociationsError(
          error?.message || "Impossible de vérifier les processus existants"
        );
      } finally {
        setAssociationsLoading(false);
      }

      try {
        if (pilotesResult.status === "rejected") {
          throw pilotesResult.reason;
        }
        setPilotes(
          getArray(pilotesResult.value, "les pilotes", true)
            .map(normalizeUser)
            .filter((option) => option.id !== null && option.id !== undefined)
        );
      } catch (error) {
        setPilotes([]);
        setPilotesError(error?.message || "Impossible de charger les pilotes");
      } finally {
        setPilotesLoading(false);
      }
    });

    return () => {
      directionRequestRef.current += 1;
    };
  }, [
    directionReloadKey,
    formData.direction_id,
    formData.processus_id,
    isEditMode,
    open,
  ]);

  const initialDirectionOption = useMemo(() => {
    if (!isEditMode || !initialValues?.direction_id) {
      return null;
    }

    return normalizeDirection(initialValues);
  }, [initialValues, isEditMode]);

  const initialReferentielOption = useMemo(() => {
    if (!isEditMode || !initialValues?.referentiel_id) {
      return null;
    }

    return normalizeReferentiel(initialValues);
  }, [initialValues, isEditMode]);

  const directionOptions = useMemo(() => {
    if (
      !initialDirectionOption ||
      directions.some((option) => sameId(option.id, initialDirectionOption.id))
    ) {
      return directions;
    }

    return [initialDirectionOption, ...directions];
  }, [directions, initialDirectionOption]);

  const referentielOptions = useMemo(() => {
    if (
      !initialReferentielOption ||
      referentiels.some((option) => sameId(option.id, initialReferentielOption.id))
    ) {
      return referentiels;
    }

    return [initialReferentielOption, ...referentiels];
  }, [initialReferentielOption, referentiels]);

  const categoryReferentiels = useMemo(
    () =>
      isEditMode || !referentielCategory?.id
        ? referentielOptions
        : referentielOptions.filter((referentiel) =>
            sameId(referentiel.categoryId, referentielCategory.id)
          ),
    [isEditMode, referentielCategory?.id, referentielOptions]
  );

  const availableReferentiels = useMemo(
    () =>
      categoryReferentiels.filter(
        (referentiel) =>
          (isEditMode &&
            sameId(referentiel.id, initialValues?.referentiel_id)) ||
          !usedReferentielIds.some((usedId) => sameId(usedId, referentiel.id))
      ),
    [categoryReferentiels, initialValues, isEditMode, usedReferentielIds]
  );

  useEffect(() => {
    if (!open || isEditMode || !referentielCategory?.id) {
      return;
    }

    setFormData((current) => {
      if (
        !current.referentiel_id ||
        categoryReferentiels.some((referentiel) =>
          sameId(referentiel.id, current.referentiel_id)
        )
      ) {
        return current;
      }

      return { ...current, referentiel_id: null };
    });
  }, [
    categoryReferentiels,
    isEditMode,
    open,
    referentielCategory?.id,
  ]);

  const selectedDirection =
    directionOptions.find((option) => sameId(option.id, formData.direction_id)) ||
    null;
  const selectedReferentiel =
    availableReferentiels.find((option) =>
      sameId(option.id, formData.referentiel_id)
    ) || null;
  const selectedPilote =
    pilotes.find((option) => sameId(option.id, formData.pilote_id)) || null;
  const selectedLeader =
    leaders.find((option) => sameId(option.id, formData.leader_smq_id)) || null;

  const initialInstance = String(initialValues?.instance || "").trim();
  const computedInstance =
    selectedDirection && selectedReferentiel
      ? `${selectedReferentiel.code}-${selectedDirection.code}`
      : "";
  const instanceCode =
    isEditMode && initialInstance ? initialInstance : computedInstance;
  const categoryName =
    selectedReferentiel?.categoryName ||
    String(initialValues?.categorie_name || "").trim();
  const referentielName =
    selectedReferentiel?.name ||
    String(initialValues?.referentiel_nom || "").trim();
  const instancePreview = instanceCode
    ? `${instanceCode} — ${referentielName || "Référentiel non renseigné"} [${
        categoryName || "Catégorie non renseignée"
      }]`
    : "Sélectionnez une direction et un référentiel.";
  const validate = () => {
    const nextErrors = {};

    if (!formData.direction_id) {
      nextErrors.direction_id = "La direction est obligatoire.";
    }
    if (!formData.referentiel_id) {
      nextErrors.referentiel_id = "Le référentiel est obligatoire.";
    }

    setErrors(nextErrors);
    if (nextErrors.direction_id) {
      directionInputRef.current?.focus();
    } else if (nextErrors.referentiel_id) {
      referentielInputRef.current?.focus();
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (saving || !validate()) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const optionalText = (value) => value.trim() || null;
      const data = {
        pilote_id: formData.pilote_id,
        leader_smq_id: formData.leader_smq_id,
        mission: optionalText(formData.mission),
        entrant: optionalText(formData.entrant),
        sortant: optionalText(formData.sortant),
        activite: optionalText(formData.activite),
        ressource: optionalText(formData.ressource),
        indicateur: optionalText(formData.indicateur),
        valeur: optionalText(formData.valeur),
      };

      if (isEditMode) {
        await ProcessusService.updateProcessus(formData.processus_id, data);
      } else {
        await ProcessusService.createProcessus({
          direction_id: formData.direction_id,
          referentiel_id: formData.referentiel_id,
          ...data,
        });
      }

      onClose?.();
      onSaved?.();
    } catch (error) {
      setSaveError(error?.message || "Impossible de créer le processus");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose?.();
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": { borderRadius: "7px", fontSize: 12 },
  };
  const autoInputSx = {
    ...inputSx,
    "& .MuiOutlinedInput-root": {
      ...inputSx["& .MuiOutlinedInput-root"],
      backgroundColor: "#edf3f8",
      color: "#52677c",
      fontWeight: 600,
    },
  };
  const textareaSx = {
    backgroundColor: saving ? "#f2f4f6" : "#fff",
    border: "1px solid #b8c8d8",
    borderRadius: "7px",
    boxSizing: "border-box",
    color: "#10263d",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    lineHeight: 1.5,
    minHeight: 76,
    outline: "none",
    p: 1.2,
    resize: "vertical",
    transition: "border-color 150ms ease, box-shadow 150ms ease",
    width: "100%",
    "&::placeholder": { color: "#788b9d", opacity: 1 },
    "&:hover:not(:disabled)": { borderColor: "#10263d" },
    "&:focus": {
      borderColor: "#1976d2",
      boxShadow: "0 0 0 1px #1976d2",
    },
    "&:disabled": { color: "#788b9d", cursor: "not-allowed" },
  };

  return (
    <Dialog
      BackdropProps={{ sx: { backgroundColor: "rgba(15, 23, 32, 0.58)" } }}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
      PaperProps={{
        component: "form",
        onSubmit: handleSave,
        sx: { borderRadius: "18px", maxHeight: "calc(100vh - 24px)" },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          borderBottom: "1px solid #cad7e5",
          color: "#10263d",
          display: "flex",
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 18,
          fontWeight: 700,
          justifyContent: "space-between",
          px: 2.5,
          py: 1.7,
        }}
      >
        {isEditMode ? "Modifier le Processus" : "Nouveau Processus"}
        <Button
          color="error"
          disabled={saving}
          onClick={handleClose}
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          sx={{ fontWeight: 700, textTransform: "none" }}
          variant="outlined"
        >
          Fermer
        </Button>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: "18px !important" }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{ border: "1px solid #cad7e5", borderRadius: "8px", p: 1.7 }}
        >
          <Grid container spacing={1.5}>
            <Grid item md={6} xs={12}>
              <FieldLabel htmlFor="processus-direction">DIRECTION *</FieldLabel>
              <Autocomplete
                disabled={isEditMode || saving}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
                loading={directionsLoading}
                loadingText="Chargement des directions…"
                noOptionsText="Aucune direction disponible"
                onChange={(_, option) => {
                  setFormData((current) => ({
                    ...current,
                    direction_id: option?.id ?? null,
                    referentiel_id: null,
                    pilote_id: null,
                  }));
                  setErrors((current) => ({
                    ...current,
                    direction_id: null,
                    referentiel_id: null,
                  }));
                }}
                options={directionOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(errors.direction_id || directionsError)}
                    helperText={errors.direction_id || directionsError}
                    id="processus-direction"
                    inputRef={directionInputRef}
                    placeholder="Sélectionner une direction"
                    size="small"
                  />
                )}
                sx={inputSx}
                value={selectedDirection}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <FieldLabel htmlFor="processus-referentiel">RÉFÉRENTIEL *</FieldLabel>
              <Autocomplete
                disabled={
                  isEditMode ||
                  !formData.direction_id ||
                  saving ||
                  associationsLoading ||
                  Boolean(associationsError)
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
                loading={referentielsLoading || associationsLoading}
                loadingText="Vérification des référentiels…"
                noOptionsText={
                  !formData.direction_id
                    ? "Sélectionnez d’abord une direction"
                    : referentielCategory?.id &&
                        categoryReferentiels.length === 0
                      ? "Aucun référentiel dans cette catégorie"
                      : referentielCategory?.id
                        ? "Tous les référentiels de cette catégorie sont déjà associés"
                        : "Tous les référentiels sont déjà associés"
                }
                onChange={(_, option) => {
                  setFormData((current) => ({
                    ...current,
                    referentiel_id: option?.id ?? null,
                  }));
                  setErrors((current) => ({ ...current, referentiel_id: null }));
                }}
                options={availableReferentiels}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(errors.referentiel_id || referentielsError)}
                    helperText={errors.referentiel_id || referentielsError}
                    id="processus-referentiel"
                    inputRef={referentielInputRef}
                    placeholder="Sélectionner un référentiel"
                    size="small"
                  />
                )}
                sx={inputSx}
                value={selectedReferentiel}
              />
              {!isEditMode && referentielCategory?.id && (
                <Typography
                  color="#60758a"
                  fontSize={10}
                  sx={{ display: "block", mt: 0.5, px: 1.2 }}
                >
                  Catégorie imposée : {referentielCategory.name || "—"}
                </Typography>
              )}
            </Grid>
          </Grid>

          {associationsError && (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => setDirectionReloadKey((value) => value + 1)}
                  size="small"
                >
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mt: 1.5 }}
            >
              {associationsError}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mt: 0 }}>
            <Grid item md={6} xs={12}>
              <FieldLabel htmlFor="processus-instance-auto">
                INSTANCE (AUTO)
              </FieldLabel>
              <TextField
                fullWidth
                id="processus-instance-auto"
                InputProps={{ readOnly: true }}
                placeholder="PROC-DIR"
                size="small"
                sx={autoInputSx}
                value={instanceCode}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FieldLabel htmlFor="processus-categorie-auto">
                CATÉGORIE (AUTO)
              </FieldLabel>
              <TextField
                fullWidth
                id="processus-categorie-auto"
                InputProps={{ readOnly: true }}
                size="small"
                sx={autoInputSx}
                value={categoryName}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel htmlFor="processus-nom-auto">
                NOM COMPLET (AUTO)
              </FieldLabel>
              <TextField
                fullWidth
                id="processus-nom-auto"
                InputProps={{ readOnly: true }}
                size="small"
                sx={autoInputSx}
                value={referentielName}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 1.5 }}>
            <Typography fontSize={11} fontWeight={700}>
              APERÇU AUTOMATIQUE
            </Typography>
            <Typography fontSize={12}>{instancePreview}</Typography>
          </Alert>

          {(directionsError || referentielsError || leadersError) && (
            <Button
              onClick={() => setBaseReloadKey((value) => value + 1)}
              size="small"
              sx={{ mt: 1, textTransform: "none" }}
            >
              Réessayer les chargements
            </Button>
          )}
        </Paper>

        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item md={6} xs={12}>
            <FieldLabel htmlFor="processus-pilote">PILOTE</FieldLabel>
            <Autocomplete
              disabled={!formData.direction_id || saving}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              loading={pilotesLoading}
              loadingText="Chargement des pilotes…"
              noOptionsText="Aucun pilote disponible"
              onChange={(_, option) =>
                setFormData((current) => ({
                  ...current,
                  pilote_id: option?.id ?? null,
                }))
              }
              options={pilotes}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(pilotesError)}
                  helperText={pilotesError}
                  id="processus-pilote"
                  placeholder="Sélectionner un pilote"
                  size="small"
                />
              )}
              sx={inputSx}
              value={selectedPilote}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <FieldLabel htmlFor="processus-leader">LEADER SMQ</FieldLabel>
            <Autocomplete
              disabled={saving}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              loading={leadersLoading}
              loadingText="Chargement des leaders…"
              noOptionsText="Aucun leader disponible"
              onChange={(_, option) =>
                setFormData((current) => ({
                  ...current,
                  leader_smq_id: option?.id ?? null,
                }))
              }
              options={leaders}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(leadersError)}
                  helperText={leadersError}
                  id="processus-leader"
                  placeholder="Sélectionner un leader SMQ"
                  size="small"
                />
              )}
              sx={inputSx}
              value={selectedLeader}
            />
          </Grid>

          {TEXT_FIELDS.map((field) => (
            <Grid item key={field.key} md={field.fullWidth ? 12 : 6} xs={12}>
              <FieldLabel htmlFor={`processus-${field.key}`}>
                {field.label}
              </FieldLabel>
              <Box
                component="textarea"
                disabled={saving}
                id={`processus-${field.key}`}
                onChange={(event) =>
                  setFormData((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
                placeholder={field.placeholder || ""}
                rows={field.rows}
                sx={textareaSx}
                value={formData[field.key]}
              />
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{ borderTop: "1px solid #cad7e5", gap: 1.2, px: 2.5, py: 1.4 }}
      >
        <Button
          color="inherit"
          disabled={saving}
          onClick={handleClose}
          sx={{ color: "#10263d", fontWeight: 700, textTransform: "none" }}
        >
          Annuler
        </Button>
        <ButtonYellow disabled={saving} type="submit">
          {saving && <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />}
          {isEditMode
            ? "✓ Enregistrer les modifications"
            : "✓ Créer cette instance PROC-DIR"}
        </ButtonYellow>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessusAdd;
